/**
 * ε-NTU va LMTD munosabatlari.
 *
 * MANBA: Incropera & DeWitt, Fundamentals of Heat and Mass Transfer, Ch. 11;
 *        Kays & London, Compact Heat Exchangers (ε-NTU jadvallar);
 *        Bowman, Mueller & Nagle (1940) — F tuzatish koeffitsienti.
 *
 * Belgilar: NTU = U·A/Cmin,  Cr = Cmin/Cmax,  ε = Q / (Cmin·(Th,in − Tc,in)).
 *
 * QABUL QILINGAN SODDALASHTIRISHLAR:
 *   - U yuza bo'ylab o'zgarmas
 *   - Cp har oqim uchun o'zgarmas (o'rtacha qiymat iteratsiya bilan topiladi)
 *   - Atrofga issiqlik yo'qotish yo'q
 *   - shell-1-2 uchun: qobiq suyuqligi har kesimda to'liq aralashgan
 */
import type { FlowArrangement } from './types';

export function effectiveness(arr: FlowArrangement, NTU: number, Cr: number): number {
  if (NTU <= 0) return 0;
  if (!Number.isFinite(NTU)) return effectivenessInfiniteNTU(arr, Cr);
  switch (arr) {
    case 'counter': {
      if (Math.abs(1 - Cr) < 1e-9) return NTU / (1 + NTU);
      const e = Math.exp(-NTU * (1 - Cr));
      return (1 - e) / (1 - Cr * e);
    }
    case 'parallel': {
      return (1 - Math.exp(-NTU * (1 + Cr))) / (1 + Cr);
    }
    case 'shell-1-2': {
      const s = Math.sqrt(1 + Cr * Cr);
      // (1 + e^{-x}) / (1 − e^{-x}) = coth(x/2)
      const coth = 1 / Math.tanh((NTU * s) / 2);
      return 2 / (1 + Cr + s * coth);
    }
  }
}

function effectivenessInfiniteNTU(arr: FlowArrangement, Cr: number): number {
  switch (arr) {
    case 'counter':
      return 1;
    case 'parallel':
      return 1 / (1 + Cr);
    case 'shell-1-2':
      return 2 / (1 + Cr + Math.sqrt(1 + Cr * Cr));
  }
}

/**
 * Teskari: ε va Cr dan NTU. Laboratoriya ishida talaba o'lchangan haroratlardan
 * U ni aniqlaydi — bu funksiya shu yo'lni beradi. Erishib bo'lmaydigan ε uchun Infinity.
 */
export function ntuFromEffectiveness(arr: FlowArrangement, eps: number, Cr: number): number {
  if (eps <= 0) return 0;
  const epsMax = effectivenessInfiniteNTU(arr, Cr);
  if (eps >= epsMax) return Infinity;
  switch (arr) {
    case 'counter': {
      if (Math.abs(1 - Cr) < 1e-9) return eps / (1 - eps);
      return (1 / (Cr - 1)) * Math.log((eps - 1) / (eps * Cr - 1));
    }
    case 'parallel':
      return -Math.log(1 - eps * (1 + Cr)) / (1 + Cr);
    case 'shell-1-2': {
      const s = Math.sqrt(1 + Cr * Cr);
      const E = (2 / eps - (1 + Cr)) / s;
      return -(1 / s) * Math.log((E - 1) / (E + 1));
    }
  }
}

/**
 * O'rtacha logarifmik harorat farqi. ΔT1 va ΔT2 — ikki uchdagi farqlar.
 * Ikkalasi ham musbat bo'lishi kerak; teng bo'lsa arifmetik o'rtacha (limit).
 */
export function lmtd(dT1: number, dT2: number): number {
  if (dT1 <= 0 || dT2 <= 0) return NaN;
  if (Math.abs(dT1 - dT2) < 1e-9 * Math.max(dT1, dT2)) return 0.5 * (dT1 + dT2);
  return (dT1 - dT2) / Math.log(dT1 / dT2);
}

/**
 * Teskari oqim asosidagi LMTD uchun uch harorat farqlari.
 * To'g'ri oqimda ΔT1 = Th,in − Tc,in, ΔT2 = Th,out − Tc,out.
 */
export function terminalDifferences(
  arr: FlowArrangement,
  ThIn: number,
  ThOut: number,
  TcIn: number,
  TcOut: number,
): { dT1: number; dT2: number } {
  if (arr === 'parallel') return { dT1: ThIn - TcIn, dT2: ThOut - TcOut };
  return { dT1: ThIn - TcOut, dT2: ThOut - TcIn };
}

/**
 * F tuzatish koeffitsienti, 1 qobiq yo'li — 2 (yoki 2n) quvur yo'li (Bowman, 1940).
 *   P = (Tc,out − Tc,in) / (Th,in − Tc,in)      (sovuq oqim "samaradorligi")
 *   R = (Th,in − Th,out) / (Tc,out − Tc,in) = Ccold / Chot
 * Formula P va R ni qaysi oqim uchun olinishiga nisbatan simmetrik.
 */
export function correctionFactorShell12(P: number, R: number): number {
  if (P <= 0) return 1;
  const s = Math.sqrt(R * R + 1);
  const denom = Math.log((2 - P * (R + 1 - s)) / (2 - P * (R + 1 + s)));
  if (Math.abs(R - 1) < 1e-9) {
    return (P * Math.SQRT2) / ((1 - P) * denom);
  }
  const num = (s / (R - 1)) * Math.log((1 - P) / (1 - P * R));
  return num / denom;
}

/** Sxemaga mos F: 1-1 uchun aynan 1 */
export function correctionFactor(
  arr: FlowArrangement,
  ThIn: number,
  ThOut: number,
  TcIn: number,
  TcOut: number,
): number {
  if (arr !== 'shell-1-2') return 1;
  const span = ThIn - TcIn;
  if (Math.abs(span) < 1e-12) return 1;
  const dTc = TcOut - TcIn;
  if (Math.abs(dTc) < 1e-12) return 1;
  const P = dTc / span;
  const R = (ThIn - ThOut) / dTc;
  return correctionFactorShell12(P, R);
}
